<template>
  <iRow
    v-if="kind === 'microphone'"
    gap="small"
    vertical-align="middle"
    wrap="wrap"
    width="fill"
    data-test="call-drawer-microphone"
  >
    <iControlDockGroup variant="outline" width="fill">
      <DeviceMenu
        ref="microphonePicker"
        tone="dock"
        icon="ic:baseline-mic"
        label="Microphone"
        :items="microphoneItems"
        :selected-id="selectedMicrophoneDeviceId"
        :empty-state="describeEmptyState(availableAudioInputDevices, 'audioinput')"
        :test-id="`${pickerTestIdPrefix}-microphone-select`"
        @select="$emit('change-microphone', $event)"
      />
    </iControlDockGroup>
    <iControlDockGroup v-if="canChooseOutput" variant="outline" width="fill">
      <DeviceMenu
        ref="speakerPicker"
        tone="dock"
        icon="ic:baseline-volume-up"
        label="Speaker"
        :items="speakerItems"
        :selected-id="selectedOutputDeviceId"
        :empty-state="describeEmptyState(availableOutputDevices, 'audiooutput')"
        :test-id="`${pickerTestIdPrefix}-speaker-select`"
        @select="$emit('change-output', $event)"
      />
    </iControlDockGroup>
    <CallControlButton
      v-if="showSettings"
      variant="quiet"
      size="compact"
      :is-toggle="false"
      label="Audio settings"
      data-test="call-drawer-microphone-settings"
      @click="$emit('open-settings')"
    >
      <iIcon name="ic:outline-settings" variant="inherit" />
    </CallControlButton>
  </iRow>
  <iRow
    v-else
    gap="small"
    vertical-align="middle"
    wrap="wrap"
    width="fill"
    data-test="call-drawer-camera"
  >
    <iControlDockGroup variant="outline" width="fill">
      <DeviceMenu
        ref="cameraPicker"
        tone="dock"
        icon="ic:baseline-videocam"
        label="Camera"
        :items="cameraItems"
        :selected-id="selectedCameraDeviceId"
        :empty-state="describeEmptyState(availableVideoDevices, 'videoinput')"
        :test-id="`${pickerTestIdPrefix}-camera-select`"
        @select="$emit('change-camera', $event)"
      />
    </iControlDockGroup>
    <CallControlButton
      v-if="showSettings"
      variant="quiet"
      size="compact"
      :is-toggle="false"
      label="Video settings"
      data-test="call-drawer-camera-settings"
      @click="$emit('open-settings')"
    >
      <iIcon name="ic:outline-settings" variant="inherit" />
    </CallControlButton>
  </iRow>
</template>

<script>
import CallControlButton from "%/apps/video-engine/components/video-call/controls/CallControlButton.vue";
import DeviceMenu from "%/apps/video-engine/components/video-call/DeviceMenu.vue";
import { describeDevices, describeEmptyState, supportsOutputSelection } from "%/apps/video-engine/device-catalog";

const DEVICE_DRAWERS = ["microphone", "camera"];

export default {
  name: "CallDeviceDrawer",
  components: { CallControlButton, DeviceMenu },
  props: {
    kind: {
      type: String,
      required: true,
      validator: kind => DEVICE_DRAWERS.includes(kind),
    },
    pickerTestIdPrefix: {
      type: String,
      required: true,
    },
    showSettings: {
      type: Boolean,
      default: true,
    },
    availableAudioInputDevices: {
      type: Array,
      default: () => [],
    },
    availableOutputDevices: {
      type: Array,
      default: () => [],
    },
    availableVideoDevices: {
      type: Array,
      default: () => [],
    },
    selectedMicrophoneDeviceId: {
      type: String,
      default: null,
    },
    selectedOutputDeviceId: {
      type: String,
      default: null,
    },
    selectedCameraDeviceId: {
      type: String,
      default: null,
    },
  },
  emits: ["change-microphone", "change-output", "change-camera", "open-settings"],
  computed: {
    microphoneItems() {
      return describeDevices(this.availableAudioInputDevices, "audioinput");
    },
    speakerItems() {
      return describeDevices(this.availableOutputDevices, "audiooutput");
    },
    cameraItems() {
      return describeDevices(this.availableVideoDevices, "videoinput");
    },
    canChooseOutput() {
      return supportsOutputSelection();
    },
  },
  methods: {
    describeEmptyState,
    pickers() {
      return ["microphonePicker", "speakerPicker", "cameraPicker"].map(name => this.$refs[name]).filter(Boolean);
    },
  },
};
</script>
